import type { ClientMessage, ServerMessage, UploadCollision } from './protocol';
import { UploadWindow } from './uploadWindow';

import { trace } from './uploadTrace';

// Mirrors the limits in packages/core/src/protocol.rs.
export const UPLOAD_WINDOW_BYTES = 4 * 1024 * 1024;
export const UPLOAD_CHUNK_MAX = 16 * 1024;
export const UPLOAD_FRAME_HEADER = 12;
export const UPLOAD_MAX_BATCH = 100;
const NAME_MAX = 255;

/** The binary frame of the upload lane: upload id (u32) and byte offset (u64), big-endian, then the payload. */
export function encodeUploadFrame(id: number, offset: number, data: Uint8Array): Uint8Array {
  if (data.length > UPLOAD_CHUNK_MAX) throw new Error('upload chunk too large');
  const out = new Uint8Array(UPLOAD_FRAME_HEADER + data.length);
  writeFrameHeader(new DataView(out.buffer), id, offset);
  out.set(data, UPLOAD_FRAME_HEADER);
  return out;
}

/** The 12 header bytes; the offset as two u32 halves, so no BigInt is allocated per chunk. */
function writeFrameHeader(view: DataView, id: number, offset: number) {
  view.setUint32(0, id);
  view.setUint32(4, Math.floor(offset / 0x100000000));
  view.setUint32(8, offset >>> 0);
}

/** Replaces what the server would refuse in a file name (separators, control characters, a leading dot, edge spaces, length). */
export function cleanUploadName(name: string): string {
  let n = name.replace(/[/\\\u0000-\u001f\u007f-\u009f]/g, '_').trim().replace(/^\.+/, m => '_'.repeat(m.length));
  const enc = new TextEncoder();
  if (enc.encode(n).length > NAME_MAX) {
    const dot = n.lastIndexOf('.');
    const ext = dot > 0 && n.length - dot <= 16 ? n.slice(dot) : '';
    let stem = [...n.slice(0, ext ? dot : n.length)];
    while (stem.length && enc.encode(stem.join('')).length + enc.encode(ext).length > NAME_MAX) stem.pop();
    n = (stem.join('') + ext).trim();
  }
  return n || 'file';
}

export type UploadState = 'confirm' | 'queued' | 'uploading' | 'paused' | 'denied' | 'failed' | 'cancelled' | 'done';

/** A file as the queue needs it: a browser `File` fits. */
export interface UploadSource {
  name: string;
  size: number;
  /** `stream` is optional: a browser `File` has it and reads far faster through it than one `arrayBuffer()` per chunk. */
  slice(start: number, end: number): { arrayBuffer(): Promise<ArrayBuffer>; stream?(): ReadableStream<Uint8Array> };
}

/** Where the bytes of the active file come from: a stream opened at `pos`, or window-sized reads when there is no stream. */
interface Reader {
  id: number;
  /** File offset of `buf[at]`, the next byte to frame. */
  pos: number;
  buf: Uint8Array;
  at: number;
  /** Next byte to fetch, for the no-stream path. */
  fetchAt: number;
  stream?: ReadableStreamDefaultReader<Uint8Array>;
}

export interface UploadItem {
  id: number;
  name: string;
  size: number;
  collision: UploadCollision;
  state: UploadState;
  received: number;
  /** The folder the server pinned; known after `upload_ready`. */
  dir?: string;
  /** The final path, after `upload_done`. */
  path?: string;
  reason?: string;
}

export interface UploadDeps {
  workspace: string;
  tab: string;
  send: (m: ClientMessage) => void;
  /** Sends one binary upload frame; false when the transport cannot carry it now. The frame is reused for the next chunk: copy it to keep it. */
  sendChunk: (frame: Uint8Array) => boolean;
  firstId?: number;
  /** A millisecond clock; `performance.now` by default. */
  now?: () => number;
}

/**
 * Files for one tab, uploaded one at a time. Nothing starts before `start()` (the user's confirmation). At most
 * the window (`UploadWindow`: 64 KiB to `UPLOAD_WINDOW_BYTES`, from how long acknowledgements take) are sent and not yet acknowledged; after a dropped link `resume()` asks the server where it got to.
 */
export class UploadQueue {
  items: UploadItem[] = [];
  private sources = new Map<number, UploadSource>();
  private nextId: number;
  private started = false;
  private sent = 0;
  private pumping = false;
  private win = new UploadWindow(UPLOAD_WINDOW_BYTES);
  private rd: Reader | null = null;
  /** One frame buffer for every chunk: `send` copies synchronously, so nothing is allocated per chunk. */
  private frame = new Uint8Array(UPLOAD_FRAME_HEADER + UPLOAD_CHUNK_MAX);
  private frameView = new DataView(this.frame.buffer);
  private defaultCollision: UploadCollision = 'keep_both';
  private listeners = new Set<() => void>();

  constructor(private deps: UploadDeps) { this.nextId = deps.firstId ?? 1; }
  private clock() { return (this.deps.now ?? performance.now.bind(performance))(); }

  subscribe(fn: () => void): () => void { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  private changed() { for (const l of this.listeners) l(); }

  /** Adds files in confirm state; returns how many fit under the batch limit. */
  add(files: UploadSource[]): number {
    let n = 0;
    for (const f of files) {
      if (this.items.length >= UPLOAD_MAX_BATCH) break;
      const id = this.nextId++;
      this.items.push({ id, name: cleanUploadName(f.name), size: f.size, collision: this.defaultCollision, state: this.started ? 'queued' : 'confirm', received: 0 });
      this.sources.set(id, f);
      n++;
    }
    this.changed();
    if (this.started) this.next();
    return n;
  }

  /** Sets the collision choice for one file; with `rest`, for every later waiting file and for files added after. */
  setCollision(id: number, collision: UploadCollision, rest = false) {
    const i = this.items.findIndex(x => x.id === id);
    if (i < 0) return;
    this.items[i].collision = collision;
    if (rest) {
      this.defaultCollision = collision;
      for (const it of this.items.slice(i + 1)) if (it.state === 'confirm' || it.state === 'queued') it.collision = collision;
    }
    this.changed();
  }

  /** The user confirmed: queue everything and begin. */
  start() {
    if (this.started) return;
    this.started = true;
    for (const it of this.items) if (it.state === 'confirm') it.state = 'queued';
    this.changed();
    this.next();
  }

  private get active(): UploadItem | undefined { return this.items.find(x => x.state === 'uploading' || x.state === 'paused'); }

  private next() {
    if (!this.started || this.active) return;
    const it = this.items.find(x => x.state === 'queued');
    if (!it) return;
    it.state = 'uploading';
    this.sent = 0;
    this.deps.send({ type: 'upload_start', id: it.id, workspace: this.deps.workspace, tab: this.deps.tab, name: it.name, size: it.size, collision: it.collision });
    this.changed();
  }

  /** Feed every server message; true when it was an upload reply. */
  handle(msg: ServerMessage): boolean {
    if (!('type' in msg) || !msg.type.startsWith('upload_')) return false;
    const it = this.items.find(x => x.id === (msg as { id: number }).id);
    if (!it) return true;
    switch (msg.type) {
      case 'upload_ready':
        it.dir = msg.dir;
        it.received = this.sent = msg.offset;
        this.win.reset(msg.offset);
        it.state = 'uploading';
        void this.pump(it);
        break;
      case 'upload_progress':
        trace('ack', { received: msg.received, sent: this.sent, inflight: this.sent - msg.received, win: UPLOAD_WINDOW_BYTES });
        it.received = Math.max(it.received, msg.received);
        this.win.onAck(msg.received, this.clock());
        if (it.size > 0 && it.received >= it.size && this.sent >= it.size) this.finish(it);
        else void this.pump(it);
        break;
      case 'upload_done':
        it.state = 'done'; it.path = msg.path; it.received = it.size;
        this.next();
        break;
      case 'upload_denied': it.state = 'denied'; it.reason = msg.reason; this.next(); break;
      case 'upload_failed': it.state = 'failed'; it.reason = msg.reason; this.next(); break;
      case 'upload_cancelled': it.state = 'cancelled'; this.next(); break;
    }
    this.changed();
    return true;
  }

  private finished = new Set<number>();
  private finish(it: UploadItem) {
    if (this.finished.has(it.id)) return;
    this.finished.add(it.id);
    this.deps.send({ type: 'upload_finish', id: it.id });
  }

  private closeReader() {
    const rd = this.rd;
    this.rd = null;
    void rd?.stream?.cancel().catch(() => {});
  }

  private openReader(it: UploadItem, src: UploadSource, pos: number): Reader {
    const rd: Reader = { id: it.id, pos, buf: new Uint8Array(0), at: 0, fetchAt: pos };
    const blob = src.slice(pos, it.size);
    if (blob.stream) rd.stream = blob.stream().getReader();
    return rd;
  }

  /** The next block of the file after `rd.fetchAt`; null at its end. */
  private async more(rd: Reader, src: UploadSource, size: number): Promise<Uint8Array | null> {
    if (rd.stream) {
      const r = await rd.stream.read();
      return r.done ? null : r.value;
    }
    if (rd.fetchAt >= size) return null;
    const end = Math.min(size, rd.fetchAt + UPLOAD_WINDOW_BYTES);
    const data = new Uint8Array(await src.slice(rd.fetchAt, end).arrayBuffer());
    rd.fetchAt = end;
    return data;
  }

  private async pump(it: UploadItem) {
    if (this.pumping) return;
    this.pumping = true;
    try {
      const src = this.sources.get(it.id);
      if (!src) return;
      if (it.size === 0) { this.finish(it); return; }
      while (it.state === 'uploading' && this.sent < it.size && this.sent - it.received < this.win.bytes) {
        const start = this.sent;
        // A resume moved the offset, or this is another file: the open stream no longer lines up.
        if (this.rd && (this.rd.id !== it.id || this.rd.pos !== start)) this.closeReader();
        const rd = this.rd ??= this.openReader(it, src, start);
        if (rd.at >= rd.buf.length) {
          let block: Uint8Array | null;
          const r0 = performance.now();
          try { block = await this.more(rd, src, it.size); } catch { block = null; }
          // A resume or cancel may have moved on while the read was pending.
          if (it.state !== 'uploading' || this.sent !== start || this.rd !== rd) continue;
          if (!block) { this.closeReader(); it.state = 'failed'; it.reason = 'The file could not be read'; this.deps.send({ type: 'upload_cancel', id: it.id }); this.next(); this.changed(); return; }
          rd.buf = block; rd.at = 0;
          trace('read', { bytes: block.length, us: Math.round((performance.now() - r0) * 1000) });
          if (!block.length) continue;
        }
        const n = Math.min(UPLOAD_CHUNK_MAX, rd.buf.length - rd.at, it.size - start, it.received + this.win.bytes - start);
        writeFrameHeader(this.frameView, it.id, start);
        this.frame.set(rd.buf.subarray(rd.at, rd.at + n), UPLOAD_FRAME_HEADER);
        if (!this.deps.sendChunk(this.frame.subarray(0, UPLOAD_FRAME_HEADER + n))) { it.state = 'paused'; this.closeReader(); this.changed(); return; }
        this.win.onSend(start + n, this.clock());
        rd.at += n; rd.pos += n;
        this.sent = start + n;
        trace('send', { bytes: n, offset: start, inflight: this.sent - it.received, win: this.win.bytes });
      }
      // Out of the loop with bytes left: the window is full and the sender waits for an ack.
      if (it.state === 'uploading' && this.sent < it.size) trace('stall', { inflight: this.sent - it.received, win: this.win.bytes });
      if (this.sent >= it.size) this.closeReader();
      this.changed();
    } finally { this.pumping = false; }
  }

  /** The connection dropped: keep the file and wait for `resume()`. */
  pause() {
    const it = this.active;
    if (it) { it.state = 'paused'; this.closeReader(); this.changed(); }
  }

  /** The connection is back: ask the server for its offset; `upload_ready` continues from there. */
  resume() {
    const it = this.active;
    if (!it) return;
    it.state = 'uploading';
    this.finished.delete(it.id);
    this.deps.send({ type: 'upload_resume', id: it.id });
    this.changed();
  }

  /** Stops the active upload, deleting its partial, and drops everything still waiting. */
  cancel() {
    const it = this.active;
    for (const x of this.items) if (x.state === 'queued' || x.state === 'confirm') x.state = 'cancelled';
    if (it) { it.state = 'cancelled'; this.closeReader(); this.deps.send({ type: 'upload_cancel', id: it.id }); }
    this.changed();
  }

  /** Puts a denied or failed file back at the end of the queue (the Retry button). */
  retry(id: number) {
    const it = this.items.find(x => x.id === id);
    if (!it || (it.state !== 'failed' && it.state !== 'denied' && it.state !== 'cancelled')) return;
    it.state = 'queued'; it.received = 0; it.reason = undefined;
    this.finished.delete(id);
    this.changed();
    this.next();
  }
}
